import {useCallback, useMemo, useState} from 'react';
import dayjs from '../../../services/customDayJs';
import {convertUserLocalTimeToUtc} from '../../../utils/dateWithTimezoneConversion';
import {Voicemail} from '../../../store/actions/payloads';
import {useSelector} from "react-redux";
import {ReduxState} from "../../../store/types";

type Args = {
    accountVoicemails: { i_account: number; voicemails: Voicemail[] }[] | undefined;
    i_account: number;
    timezoneOffset: number;
    userDateTimeFormat: string;
    defaultPageSize?: number;
};

export type GetVoicemailsFn = (formData: { from?: string; till?: string }, limit?: number, offset?: number, resetPage?: boolean) => void;


const parseCallerInfo = (from: string): { number: string; name: string } => {
    const newFormatMatch = from.match(/^(\d+)(?:\s+\(([^)]+)\))?\s+<.+>$/);

    if (newFormatMatch) {
        return {
            number: newFormatMatch[1],
            name: newFormatMatch[2] || '',
        };
    }

    const hashFormatMatch = from.match(/#([^\s<]+)\s*</);

    if (hashFormatMatch) {
        return {
            number: hashFormatMatch[1],
            name: '',
        };
    }

    const oldFormatMatch = from.match(/(ph\d+x\d+)\s+</);

    if (oldFormatMatch) {
        return {
            number: oldFormatMatch[1],
            name: '',
        };
    }

    // Fallback
    return {
        number: from,
        name: '',
    };
};

export const useVoicemailsData = (
    {
        accountVoicemails,
        i_account,
        timezoneOffset,
        userDateTimeFormat,
        defaultPageSize = 10,
    }: Args) => {
    const [voicemails, setVoicemails] = useState<Voicemail[] | undefined>(undefined);
    const [filteredCount, setFilteredCount] = useState<number>(0);
    const [forceFirstPage, setForceFirstPage] = useState(false);

    const {isLoadingAccountDetails, accountDetails
    } = useSelector((state: ReduxState) => state.accountControlPanel);

    const voicemailsNoPaging = useMemo(
        () => {
            if (!i_account) {
                return accountVoicemails?.length ? accountVoicemails[0].voicemails ?? [] : [];
            }
            return accountVoicemails?.find((e) => e.i_account === i_account)?.voicemails ?? [];
        },
        [accountVoicemails, i_account],
    );

    const getVoicemails: GetVoicemailsFn = useCallback(
        (formData, _limit?, _offset?, resetPage = true) => {
            const backendTimeFormat = 'DD-MMM-YYYY HH:mm:ss Z';
            let filtered = [...voicemailsNoPaging];

            // sort desc by delivery_date
            filtered.sort((a, b) =>
                dayjs(b.delivery_date, backendTimeFormat).isAfter(dayjs(a.delivery_date, backendTimeFormat)) ? 1 : -1
            );

            if (formData?.from) {
                const fromdjs = dayjs.utc(convertUserLocalTimeToUtc(formData.from, timezoneOffset), userDateTimeFormat);
                filtered = filtered.filter((e) => !e.delivery_date || dayjs(e.delivery_date, backendTimeFormat).isAfter(fromdjs));
            }

            if (formData?.till) {
                const tilljs = dayjs.utc(convertUserLocalTimeToUtc(formData.till, timezoneOffset), userDateTimeFormat);
                filtered = filtered.filter((e) => !e.delivery_date || tilljs.isAfter(dayjs(e.delivery_date, backendTimeFormat)));
            }

            if(!i_account)
            {

                filtered = filtered.map((e) => {

                    const callerInfo = parseCallerInfo(e?.from || '');
                    const destinationInfo = parseCallerInfo(e?.to || '');

                    const callerToDisplay = callerInfo.number ? callerInfo.number && callerInfo.name ? `${callerInfo.number} - ${callerInfo.name}` : callerInfo.number : '';
                    const destinationToShow = accountDetails && destinationInfo?.number ? accountDetails?.id === destinationInfo.number ?  `${accountDetails.extension_id} - ${accountDetails.extension_name}` : destinationInfo.number : '';

                    const accountDetailsArray = accountDetails?.extension_id && accountDetails?.extension_name
                        ? [accountDetails.extension_id, accountDetails.extension_name]
                        : [];

                    const callerSplitsArray = callerInfo.number && callerInfo.name
                        ? [callerInfo.number, callerInfo.name]
                        : [];

                    return {
                        ...e,
                        caller: callerToDisplay,
                        destination: destinationToShow,
                        destination_splits:accountDetailsArray,
                        caller_splits:callerSplitsArray,
                    }

                });
            }

            setFilteredCount(filtered.length);
            setVoicemails(filtered);
            if (resetPage) {
                setForceFirstPage(true);
            }
        },
        [voicemailsNoPaging, timezoneOffset, userDateTimeFormat, defaultPageSize, isLoadingAccountDetails, accountDetails]
    );

    return {
        voicemails,
        filteredCount,
        getVoicemails,
        forceFirstPage,
        setForceFirstPage,
    };
}

